import { formatLeadTimeWeeks } from '@lib/calculations/lead-time/calculate-weeks';
import { Text, View } from '@react-pdf/renderer';

import {
  PDFQuoteLinePartType,
  PDFQuoteLineType,
  usePDFContext,
} from '../../../PDFProvider';
import { formatLineNumber } from '../../utils/format-line-number';
import { getLeadTimeDisplay } from '../../utils/lead-time-display';
import { styles } from '../styles';
import { PartInformation } from './PartInformation';
import { QuoteLineDetails } from './QuoteLineDetails';

type QuoteLineForPartProps = Omit<PDFQuoteLineType, 'part'> & {
  part: PDFQuoteLinePartType;
} & {
  showDrawing?: boolean;
  children: React.ReactNode;
  bottomOfLineText?: string;
  /** For documents that communicate lead time themselves, e.g. BTL. */
  hideLeadTime?: boolean;
};

export function QuoteLineForPart({
  bottomOfLineText,
  lineNumber,
  children,
  hideLeadTime = false,
  isQuantityAwareLeadTime,
  leadTimeWeeks,
  leadTimeNotes,
  part,
  quotePrices,
  showDrawing = false,
}: QuoteLineForPartProps) {
  const { pricingUnit, leadTimeFormat } = usePDFContext();

  const { lineText, showPriceColumn } = hideLeadTime
    ? { lineText: null, showPriceColumn: false }
    : getLeadTimeDisplay({
        quotePrices,
        leadTimeNotes,
        leadTimeFormat,
        isQuantityAwareLeadTime,
        leadTimeWeeks,
      });

  return (
    <View style={[styles.gap10, styles.column, styles.wrapper]} wrap={false}>
      <View style={[styles.gap10, styles.row]}>
        <View style={styles.column}>
          <Text>Line Number {formatLineNumber(lineNumber)}</Text>
        </View>

        <PartInformation>{children}</PartInformation>

        <View style={[styles.column, styles.leftColumnWrapper]}>
          <QuoteLineDetails
            drawingId={part.drawingId}
            drawingRevision={part.drawingRevision}
            leadTimeText={lineText}
            showDrawing={showDrawing}
          />

          <View
            style={[
              styles.gap10,
              styles.row,
              styles.widthFull,
              { justifyContent: 'space-between' },
            ]}
          >
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>Quantity</Text>
              {quotePrices.map(({ id, quantity }) => (
                <Text key={`${id}-${quantity}`}>{quantity}</Text>
              ))}
            </View>
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>{pricingUnit}</Text>
              {quotePrices.map(({ id, unitPrice }) => (
                <Text key={`${id}-${unitPrice}`}>{unitPrice}</Text>
              ))}
            </View>
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>Line Total</Text>
              {quotePrices.map(({ id, total }) => (
                <Text key={`${id}-${total}`}>{total}</Text>
              ))}
            </View>
            {showPriceColumn && (
              <View style={[styles.alignEnd, styles.column, styles.gap4]}>
                <Text style={styles.underline}>Lead Time</Text>
                {quotePrices.map(({ id, leadTimeWeeks }) => (
                  <Text key={`${id}-lead-time`}>
                    {leadTimeWeeks ? formatLeadTimeWeeks(leadTimeWeeks) : '-'}
                  </Text>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>

      {bottomOfLineText && (
        <View style={[styles.gap10, styles.row]}>
          <Text style={styles.textSmall}>{bottomOfLineText}</Text>
        </View>
      )}
    </View>
  );
}
